import {get, reduce} from 'lodash';
import { getBasicConfig$Cache } from "@/common/service/common";
import {
  ON_SESSION_PAYMENT_SUPPORTED_FLOWS,
  UPI_PAYMENT_METHOD_UNSUPPORTED_FLOWS,
  UPI_PAYMENT_METHOD_UNSUPPORTED_PLAN_TYPES,
  ZUORA_OUTAGE_ALTER_CC_SUPPORTED_FLOWS,
  THEME, UPI_MAX_MANDATE_AMOUNT,
} from '@/views/checkout/constant';
import { payMethodMap } from '@/views/checkout/payment/helper/constant';
import { pageConfig } from '@/common/service/page-config';
import { isInrCurrency } from '@/utils/currency';
import { checkIfPaidToPaidFlow } from '@/views/checkout/payment/helper/util';

const getDefaultIpLocation = () => {
  return {
    cn: "",
    countryName: "",
    region: "",
    regionName: "",
    city: "",
    postalCode: "",
    locationId: undefined,
  }
}

const disabledCurrencyFilterTypeMap = {
  categoryCode: 1,
  planCode: 2,
}

// disabled parent categoryCode or planCode with currency list
const transferDisabledCurrencyList = (disableCurrencyList) => {
  return reduce(disableCurrencyList, (res, item) => {
    const codeType = item?.filterType;

    if (codeType === disabledCurrencyFilterTypeMap.categoryCode) {
      res.disableCurrency4PlanCategory[item?.code] = item?.currencyList;
    } else if (codeType === disabledCurrencyFilterTypeMap.planCode) {
      res.disableCurrency4PlanCode[item?.code] = item?.currencyList;
    }

    return res;
  }, {
    disableCurrency4PlanCategory: {},
    disableCurrency4PlanCode: {},
  })
}

export const COMMON_ACTION_TYPES = {
  basicConfig: '/basic/config',
  initPage: '/init/page',
}

const state = {
  basicConfigReady: false,
  clientWebViewTheme: THEME.LIGHT,
  ipLocation: getDefaultIpLocation(),
  ttrTaxExemptionEnabled: false,
  enableProMonIncrease: false,
  enableProAnnualIncrease: false,
  enableTranslation: false,
  indiaMandateAmount: '',
  indiaMaxMandateAmount: '',
  indiaMandateFactor: '',
  onSessionPaymentEnabled: false,
  inrCitEnabled: false, // feature flag
  disableCurrency4PlanCategory: {},
  enableBizPriceIncrease: false,
  globalErrorState: false,
  enableZPSms2FAVerification: false,
  blacklistedCountriesFor2FA: [],
  showInrNewTableContent: false,
  upiPaymentEnabled: false,
  upiPaymentGateway: '',
  upiAsOneTimePayment: false,
  eduDomainList: [],
  govDomainList: [],
  enableServerNextChargeDateLogic: false,
  enableServerNextChargeDateLogicPhase2: false,
  disableExpireCredits: false,
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  isZuoraOutage: false,
  lnProfile: {
    domain: null,
    orgId: null,
    sessionId: null
  },
  disableBusinessVerification: false,
  japanIP: false,
}

const getters = {
  basicConfigReady: (state) => state.basicConfigReady,
  clientWebViewTheme: (state) => state.clientWebViewTheme,
  ipLocation: (state) => state.ipLocation,
  ttrTaxExemptionEnabled: (state) => state.ttrTaxExemptionEnabled,
  enableProMonIncrease: (state) => state.enableProMonIncrease,
  enableProAnnualIncrease: (state) => state.enableProAnnualIncrease,
  enableTranslation: (state) => state.enableTranslation,
  flowType: (state)=>state.flowType,
  disableCurrency4PlanCategory: (state) => state.disableCurrency4PlanCategory,
  indiaMandateAmount: (state) => state.indiaMandateAmount,
  indiaMaxMandateAmount: (state) => state.indiaMaxMandateAmount,
  indiaMinMandateAmount: (state) => state.indiaMinMandateAmount,
  indiaMandateFactor: (state) => state.indiaMandateFactor,
  onSessionPaymentEnabled: (state) => state.onSessionPaymentEnabled,
  enableBizPriceIncrease: (state) => state.enableBizPriceIncrease,
  inrCitEnabled: (state) => state.inrCitEnabled,
  globalErrorState: (state) => state.globalErrorState,
  enableZPSms2FAVerification: (state) => state.enableZPSms2FAVerification,
  blacklistedCountriesFor2FA: (state) => state.blacklistedCountriesFor2FA,
  showInrNewTableContent: (state) => state.showInrNewTableContent,
  upiPaymentEnabled: (state, getters) => {
    const { getSelectedPlans, basicConfig, isInrCitEligible} = getters;
    if (!state.upiPaymentEnabled) {
      return false;
    }
    // Disable UPI for Direct Sales customers
    if (basicConfig?.onlineSupportedAccount === false) {
      return false;
    }
    // Check UPI P2P flag
    const showUpiPaymentForP2P = get(window.campaignFlags, 'showUpiPaymentForP2P', false);
    const upiPaymentEnabledForP2P = showUpiPaymentForP2P && basicConfig?.upiPaymentEnabledForP2P;
    if (checkIfPaidToPaidFlow(window.$context.flow) && !upiPaymentEnabledForP2P) {
      return false;
    }
    // Disable UPI for P2P MIT flow
    if (checkIfPaidToPaidFlow(window.$context.flow) && !isInrCitEligible) {
      return false;
    }
    // Disable UPI for VideoSDK plan when it's the only plan in the cart
    if(getSelectedPlans?.length === 1 && UPI_PAYMENT_METHOD_UNSUPPORTED_PLAN_TYPES.indexOf(getSelectedPlans[0]?.type) !== -1) {
      return false;
    }
    // Disable UPI for BuyNow when total amount > 15k
    if (!checkIfPaidToPaidFlow(window.$context.flow)) {
      const { getTotalCharge, getTaxTotalAmount } = getters;
      const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
      if (totalChargeToday >= UPI_MAX_MANDATE_AMOUNT) {
        return false;
      }
    }
    return true;
  },
  upiPaymentGateway: (state) => state.upiPaymentGateway,
  upiAsOneTimePayment: (state, getters) => {
    const { selectedPaymentMethod } = getters;
    if (!state.upiAsOneTimePayment || selectedPaymentMethod !== payMethodMap.upi) {
      return false;
    }

    // in case of INR CIT use pre-calculated amount
    const { isInrCitEligible, isProratedChargeAmountCalculated } = getters;
    if (isInrCitEligible && isProratedChargeAmountCalculated) {
      const { getProratedChargeTotalAmount } = getters;
      return (getProratedChargeTotalAmount / 100) >= UPI_MAX_MANDATE_AMOUNT;
    }

    // Check that total amount is greater than 15k
    const { getTotalCharge, getTaxTotalAmount } = getters;
    const totalChargeToday = (getTotalCharge + getTaxTotalAmount) / 100;
    return totalChargeToday >= UPI_MAX_MANDATE_AMOUNT;
  },
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  isZuoraOutage: (state) => state.isZuoraOutage,
  alternativeCreditCardEnabled: (state) => {
    if(!state.isZuoraOutage) {
      return false;
    }
    return ZUORA_OUTAGE_ALTER_CC_SUPPORTED_FLOWS.includes(pageConfig.type);
  },
  eduDomainList: state => state.eduDomainList,
  eduDomainRegList: state => state.eduDomainList.map(domain => {
    if (domain.indexOf('$') !== -1) {
      return new RegExp(domain, 'i');
    } else {
      return new RegExp(`${domain.replace(/(?=\.)/g, '\\')}$`, 'i');
    }
  }),
  govDomainList: state => state.govDomainList,
  govDomainRegList: state => state.govDomainList.map(domain => {
    if (domain.indexOf('$') !== -1) {
      return new RegExp(domain, 'i');
    } else {
      return new RegExp(`${domain.replace(/(?=\.)/g, '\\')}$`, 'i');
    }
  }),
  lnProfile: (state) => state.lnProfile, // LexisNexis ThreatMetrix profile data
  enableServerNextChargeDateLogic: state => state.enableServerNextChargeDateLogic,
  enableServerNextChargeDateLogicPhase2: state => state.enableServerNextChargeDateLogicPhase2,
  disableExpireCredits: state => state.disableExpireCredits,
  disableBusinessVerification: state => state.disableBusinessVerification,
}

const mutations = {
  setClientWebViewTheme (state, value) {
    state.clientWebViewTheme = value;
  },
  setIpLocation(state, data) {
    state.ipLocation = data;
  },
  setTtrTaxExemptionEnabled(state, data) {
    state.ttrTaxExemptionEnabled = data;
  },
  setEnableProMonIncrease(state, isEnable) {
    state.enableProMonIncrease = !!isEnable;
  },
  setDisableCurrency4PlanCategory(state, data = {}) {
    state.disableCurrency4PlanCategory = data;
  },
  setEnableBizPriceIncrease(state, isEnable) {
    state.enableBizPriceIncrease = !!isEnable;
  },
  setOnSessionPaymentEnabled(state, isEnable) {
    state.onSessionPaymentEnabled = !!isEnable;
  },
  setGlobalErrorState(state, data) {
    state.globalErrorState = data;
  },
  // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
  setZuoraOutage(state, isOutage) {
    state.isZuoraOutage = !!isOutage;
  },
  setEduDomainList(state, domainList) {
    state.eduDomainList = domainList;
  },
  setGovDomainList(state, domainList) {
    state.govDomainList = domainList
  },
  setEnableZPSms2FAVerification(state, data) {
    state.enableZPSms2FAVerification = data;
  },
}

const actions = {
  async [COMMON_ACTION_TYPES.initPage]({dispatch}) {
    try {
      await Promise.all([dispatch(COMMON_ACTION_TYPES.basicConfig), dispatch("getBasicConfig")]);
    } catch (e) {
      console.error(e);
    }
  },
  async [COMMON_ACTION_TYPES.basicConfig]({state, commit}) {
    const {result = getDefaultIpLocation()} = await getBasicConfig$Cache();
    const {
      iplocation,
      disableCurrencyList = [],
      ttrTaxExemptionEnabled = false,
      enableProMonIncrease = false,
      enableProAnnualIncrease = false,
      enableTranslation = false,
      isIndiaCustomerNonINR = false,
      enableCycleLimit = false,
      indiaMandateAmount = '',
      indiaMaxMandateAmount = '',
      indiaMinMandateAmount = '',
      indiaMandateFactor =  '',
      onSessionPaymentEnabled = false,
      inrCitEnabled = false,
      leadFormSupportPlanCodes,
      enableBizPriceIncrease = false,
      blacklistedCountriesFor2FA = [],
      showInrNewTableContent = false,
      upiPaymentEnabled = false,
      upiPaymentGateway = '',
      upiAsOneTimePayment = false,
      eduDomainList = [],
      govDomainList = [],
      lnProfile = {},
      enableServerNextChargeDateLogic = false,
      enableServerNextChargeDateLogicPhase2 = false,
      disableExpireCredits = false,
      disableBusinessVerification = false,
      japanIP = false,
    } = result;
    const { disableCurrency4PlanCategory } = transferDisabledCurrencyList(disableCurrencyList);

    commit('setIpLocation', iplocation);
    commit('setTtrTaxExemptionEnabled', ttrTaxExemptionEnabled);
    commit('setDisableCurrency4PlanCategory', disableCurrency4PlanCategory);
    commit('setLeadCreationSupportedPlans', leadFormSupportPlanCodes);
    commit('setEduDomainList', eduDomainList);
    commit('setGovDomainList', govDomainList);
    // update flag
    state.enableProMonIncrease = enableProMonIncrease;
    state.enableProAnnualIncrease = enableProAnnualIncrease;
    state.enableTranslation = enableTranslation;
    state.isIndiaCustomerNonINR = isIndiaCustomerNonINR;
    state.enableCycleLimit = enableCycleLimit;
    state.indiaMandateAmount = indiaMandateAmount;
    state.indiaMaxMandateAmount = indiaMaxMandateAmount;
    state.indiaMinMandateAmount = indiaMinMandateAmount;
    state.indiaMandateFactor = indiaMandateFactor;
    state.onSessionPaymentEnabled = onSessionPaymentEnabled && isInrCurrency() && ON_SESSION_PAYMENT_SUPPORTED_FLOWS.includes(pageConfig.type);
    state.inrCitEnabled = inrCitEnabled && isInrCurrency();
    state.enableBizPriceIncrease = enableBizPriceIncrease;
    state.blacklistedCountriesFor2FA = blacklistedCountriesFor2FA;
    state.showInrNewTableContent = showInrNewTableContent;
    state.upiPaymentGateway = upiPaymentGateway;
    state.upiAsOneTimePayment = upiAsOneTimePayment;
    state.lnProfile = lnProfile;
    state.enableServerNextChargeDateLogic = enableServerNextChargeDateLogic;
    state.enableServerNextChargeDateLogicPhase2 = enableServerNextChargeDateLogicPhase2;
    state.disableExpireCredits = disableExpireCredits;
    state.disableBusinessVerification = disableBusinessVerification;
    state.japanIP = japanIP;

    const showUpiPayment = get(window.campaignFlags, 'showUpiPayment', false);
    state.upiPaymentEnabled = showUpiPayment && upiPaymentEnabled && isInrCurrency() && !UPI_PAYMENT_METHOD_UNSUPPORTED_FLOWS.includes(pageConfig.type);

    state.basicConfigReady = true
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
}
